"use client";

import { useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useRouter } from "next/navigation";

import { toast } from "sonner";

import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormMessage,
} from "@/components/ui/form";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { InputForm } from "@/components/custom/forms/input-items";
import SelectForm from "@/components/custom/forms/select-items";
import CancelConfirmationDialog from "@/modules/account/user/components/pop-up/CancelConfirmationDialog";

import { Typography } from "@/components/custom/typography";
import { useCommonCDByCodes } from "@/hooks/useCommonCD";
import { InquiryRequest } from "@/types/service-inquiry";
// Schema
const inquirySchema = z.object({
  userType: z.string({ required_error: "validation.userType" }),
  subType: z.string({ required_error: "validation.subType" }),
  emailUser: z.string().min(1, "validation.emailUser"),
  emailDomain: z.string().min(1, "validation.emailDomain"),
  emailSelect: z.string().optional(),
  title: z.string().min(1, "validation.title"),
  detail: z.string().min(1, "validation.detail"),
});

type InquiryFormType = z.infer<typeof inquirySchema>;
import { ORGANIZATIONS_TYPE } from "@/lib/constants";
import { useCreateInquiry } from "@/hooks/inquiry-hooks/useCreateInquiries";
import { environments } from "@/lib/config";
import Information from "@/components/custom/infor-ad/Information";

export interface ServiceInquiryFormProps {
  label: {
    title: string;
    description: string;
    noticeLoginRequired: string;
    noticeLoginAction: string;
    noticeLoginPrefix: string;
    noticeLoginSuffix: string;
    inquiryTypeTitle: string;
    userTypeLabel: string;
    userTypePlaceholder: string;
    userTypeAdvertiser: string;
    userTypeMedia: string;
    categoryLabel: string;
    categoryPlaceholder: string;
    emailTitle: string;
    emailLabel: string;
    emailPlaceholder: string;
    emailDomainPlaceholder: string;
    emailDomainInputPlaceholder: string;
    emailHelp: string;
    domainCustom: string;
    contentTitle: string;
    subjectLabel: string;
    subjectPlaceholder: string;
    messageLabel: string;
    messagePlaceholder: string;
    actionCancel: string;
    actionSubmit: string;
  };
}

export default function ServiceInquiryForm({ label }: ServiceInquiryFormProps) {
  const router = useRouter();
  const authServerUrl = environments.authServerUrl;

  const { mutate: submitInquiry, isPending } = useCreateInquiry();

  const form = useForm<InquiryFormType>({
    resolver: zodResolver(inquirySchema),
    mode: "onChange",
    defaultValues: {
      userType: "",
      subType: "",
      emailUser: "",
      emailDomain: "",
      title: "",
      detail: "",
    },
  });

  const userType = useWatch({ control: form.control, name: "userType" });
  const subType = useWatch({ control: form.control, name: "subType" });

  const { data: dataRes } = useCommonCDByCodes(["service_inquiry_category"]);
  const options =
    dataRes?.data
      ?.filter((item) => {
        if (userType === ORGANIZATIONS_TYPE.adCompany) {
          return item.grp_sub_cd !== "media_registration_partnership";
        }
        if (userType === ORGANIZATIONS_TYPE.media) {
          return [
            "signup_inquiry",
            "media_recommendation",
            "media_registration_partnership",
            "security_privacy",
            "data_request",
            "errors_improvement_suggest",
            "other_inquiry",
          ].includes(item.grp_sub_cd);
        }
        return true;
      })
      .map((item) => ({
        label: item.common_cd_name,
        value: item.grp_sub_cd,
      })) ?? [];

  useEffect(() => {
    if (userType && options.length > 0) {
      const current = form.getValues("subType");
      if (!current) {
        form.setValue("subType", options[0].value, { shouldDirty: false });
      }
    }
  }, [userType, options, form]);
  const isDirty = form.formState.isDirty;

  const emailSelect = useWatch({ control: form.control, name: "emailSelect" });

  useEffect(() => {
    if (emailSelect && emailSelect !== "custom") {
      form.setValue("emailDomain", emailSelect);
    } else if (emailSelect === "custom") {
      form.setValue("emailDomain", "");
    }
  }, [emailSelect, form]);

  const onSubmit = (data: InquiryFormType) => {
    const request: InquiryRequest = {
      user_type_cd: data.userType,
      inquiry_category_cd: data.subType,
      non_member_email: `${data.emailUser}@${data.emailDomain}`,
      title: data.title,
      content: data.detail,
    };
    submitInquiry(request, {
      onSuccess: () => {
        toast("문의 사항이 접수되었습니다. 이전 페이지로 이동합니다.");
        setTimeout(() => router.back(), 2000);
      },
      onError: (error: Error) => {
        toast.error(`문의 실패: ${error.message}`);
      },
    });
  };

  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const isSubmitEnabled =
    form.formState.isValid && !!userType && !!subType && !isPending;

  return (
    <div className="min-w-3xl mx-auto ">
      <Card className="border-0 shadow-none p-0 gap-12">
        <CardHeader className="p-0 gap-3">
          <CardTitle className="text-4xl text-gray-900 font-bold leading-120 ">
            {label.title}
          </CardTitle>
          <CardDescription className="text-base whitespace-pre-line font-semibold">
            {label.description}
          </CardDescription>
        </CardHeader>

        <Information
          description={label.noticeLoginRequired}
          showLoginLink={true}
          loginLinkText={label.noticeLoginAction}
          classDescription="text-gray-500"
          prefixText={label.noticeLoginPrefix}
          suffixText={label.noticeLoginSuffix}
          onLoginClick={() => {
            if (isDirty) {
              setOpenCancelDialog(true);
            } else {
              router.push(authServerUrl ?? "/account/login");
            }
          }}
        />

        <CardContent className="p-0">
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-9">
              <div className="flex flex-col gap-5">
                <Typography variant="subheading2" tone="darkstrong">
                  {label.inquiryTypeTitle}
                </Typography>
                <div>
                  <Typography variant="detail1">
                    {label.userTypeLabel}{" "}
                    <Typography variant="detail1" className="text-red-400">
                      *
                    </Typography>
                  </Typography>
                  <SelectForm
                    name="userType"
                    control={form.control}
                    options={[
                      {
                        label: label.userTypeAdvertiser,
                        value: ORGANIZATIONS_TYPE.adCompany,
                      },
                      {
                        label: label.userTypeMedia,
                        value: ORGANIZATIONS_TYPE.media,
                      },
                    ]}
                    placeholder={label.userTypePlaceholder}
                    width="w-full"
                    keyT={"serviceInquiry"}
                  />
                </div>

                <div>
                  <Typography variant="detail1">
                    {label.categoryLabel}{" "}
                    <Typography variant="detail1" className="text-red-400">
                      *
                    </Typography>
                  </Typography>
                  <SelectForm
                    name="subType"
                    control={form.control}
                    options={options}
                    placeholder={label.categoryPlaceholder}
                    width="w-full"
                    disabled={!userType}
                    keyT={"serviceInquiry"}
                  />
                </div>
              </div>

              <div className="flex flex-col gap-5">
                <Typography variant="subheading2" tone="darkstrong">
                  {label.emailTitle}
                </Typography>
                <div>
                  <Typography variant="detail1">
                    {label.emailLabel}{" "}
                    <Typography variant="detail1" className="text-red-400">
                      *
                    </Typography>
                  </Typography>
                  <div className="flex gap-2 items-center w-[600px]">
                    <div className="flex-1">
                      <InputForm
                        name="emailUser"
                        control={form.control}
                        placeholder={label.emailPlaceholder}
                        type="text"
                        keyT={"serviceInquiry"}
                      />
                    </div>
                    <span className="pt-2 text-base font-normal text-gray-900">
                      @
                    </span>
                    <div className="flex-1">
                      <SelectForm
                        name="emailSelect"
                        control={form.control}
                        options={[
                          { label: "naver.com", value: "naver.com" },
                          { label: "gmail.com", value: "gmail.com" },
                          { label: "hanmail.net", value: "hanmail.net" },
                          { label: "yahoo.com", value: "yahoo.com" },
                          { label: label.domainCustom, value: "custom" },
                        ]}
                        placeholder={label.emailDomainPlaceholder}
                        keyT={"serviceInquiry"}
                      />
                    </div>
                    <div className="flex-1">
                      <InputForm
                        name="emailDomain"
                        control={form.control}
                        placeholder={label.emailDomainInputPlaceholder}
                        type="text"
                        disabled={emailSelect !== "custom"}
                        keyT={"serviceInquiry"}
                      />
                    </div>
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    {label.emailHelp}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-5">
                <Typography variant="detail1">{label.contentTitle}</Typography>
                <div>
                  <Typography
                    as="span"
                    className="text-gray-700 text-xs mb-1 font-normal"
                  >
                    {label.subjectLabel}{" "}
                    <Typography variant="detail1" className="text-red-400">
                      *
                    </Typography>
                  </Typography>
                  <InputForm
                    control={form.control}
                    name="title"
                    type="text"
                    placeholder={label.subjectPlaceholder}
                    keyT={"serviceInquiry"}
                  />
                </div>
                <div className="max-w-3xl">
                  <Typography
                    as="span"
                    className="text-gray-700 text-xs mb-1 font-normal"
                  >
                    {label.messageLabel}{" "}
                    <Typography variant="detail1" className="text-red-400">
                      *
                    </Typography>
                  </Typography>
                  <FormField
                    control={form.control}
                    name="detail"
                    render={({ field }) => (
                      <FormItem>
                        <FormControl>
                          <Textarea
                            {...field}
                            maxLength={5000}
                            placeholder={label.messagePlaceholder}
                            className="border border-gray-400 rounded-xl h-40 p-3 resize-none break-words overflow-hidden placeholder:text-base"
                          />
                        </FormControl>
                        <FormMessage keyT="serviceInquiry" />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <div className="flex gap-3.5 justify-end mt-3">
                <Button
                  type="button"
                  variant="secondary"
                  tone="light"
                  size="lg"
                  className="border-2 border-gray-300 text-base"
                  onClick={() => setOpenCancelDialog(true)}
                >
                  {label.actionCancel}
                </Button>

                <CancelConfirmationDialog
                  open={openCancelDialog}
                  onClose={() => setOpenCancelDialog(false)}
                  onConfirm={() => {
                    setOpenCancelDialog(false);
                    router.back();
                  }}
                />

                <Button
                  type="submit"
                  size="lg"
                  className="text-base"
                  disabled={!isSubmitEnabled}
                >
                  {label.actionSubmit}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>
    </div>
  );
}
